import axios from 'axios'

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json'
  }
})

// Interceptor para agregar token de autenticación
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => Promise.reject(error)
)

// Interceptor para manejar errores
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      // Notificamos a AuthContext vía CustomEvent para que limpie el estado
      // y haga navigate('/login') sin recargar la página. Antes hacíamos
      // window.location.href = '/login', que con varias requests en paralelo
      // disparaba múltiples redirects y un parpadeo perceptible.
      localStorage.removeItem('token')
      window.dispatchEvent(new CustomEvent('sesion-cerrada'))
    }
    if (error.response?.status === 403 && error.response?.data?.codigo === 'EMPRESA_BLOQUEADA') {
      window.dispatchEvent(new CustomEvent('empresa-bloqueada'))
    }
    if (error.response?.status === 403 && error.response?.data?.codigo === 'PLAN_INSUFICIENTE') {
      window.dispatchEvent(new CustomEvent('plan-insuficiente', { detail: error.response.data }))
    }
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error)
    }
    throw error
  }
)

// Empleados
export const empleadosAPI = {
  getAll: () => api.get('/empleados'),
  getById: (id) => api.get(`/empleados/${id}`),
  create: (data) => api.post('/empleados', data),
  update: (id, data) => api.put(`/empleados/${id}`, data),
  delete: (id) => api.delete(`/empleados/${id}`)
}

// Tipos de Pausa
export const tiposPausaAPI = {
  getAll: () => api.get('/tipos-pausa'),
  getById: (id) => api.get(`/tipos-pausa/${id}`),
  create: (data) => api.post('/tipos-pausa', data),
  update: (id, data) => api.put(`/tipos-pausa/${id}`, data),
  delete: (id) => api.delete(`/tipos-pausa/${id}`)
}

// Auditoría
export const auditoriaAPI = {
  getFichajes: (params) => api.get('/auditoria/fichajes', { params }),

  exportarPDF: async (params) => {
    const response = await api.get('/auditoria/fichajes/exportar-pdf', {
      params,
      responseType: 'blob'
    })

    const blob = new Blob([response.data], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `auditoria_fichajes_${params.fechaInicio}_${params.fechaFin}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    return response
  }
}

// Autenticación
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  loginPin: (pin, empresaId) => api.post('/auth/login-pin', { pin, empresa_id: empresaId }),
  logout: () => api.post('/auth/logout'),
  verify: () => api.get('/auth/verify'),
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
  adminChangePassword: (empleadoId, newPassword) =>
    api.post('/auth/admin/change-password', { empleadoId, newPassword })
}

// Empresas (Super Admin)
export const empresasAPI = {
  getAll: () => api.get('/empresas'),
  getById: (id) => api.get(`/empresas/${id}`),
  create: (data) => api.post('/empresas', data),
  update: (id, data) => api.put(`/empresas/${id}`, data),
  delete: (id) => api.delete(`/empresas/${id}`),
  getEmpleados: (id) => api.get(`/empresas/${id}/empleados`),
  getStats: (id) => api.get(`/empresas/${id}/stats`),
  getEmpleadosGlobal: () => api.get('/empresas/empleados-global/all'),
  getHistorialLicencias: (id) => api.get(`/empresas/${id}/historial-licencias`),
  updateSubscription: (id, data) => api.put(`/empresas/${id}/subscription`, data)
}

// Configuración Global (Super Admin)
export const configuracionGlobalAPI = {
  get: (clave) => api.get(`/configuracion-global/${clave}`),
  update: (data) => api.put('/configuracion-global', data)
}

// Control de Fichajes (días sin fichaje)
export const controlFichajesAPI = {
  getPendientes: () => api.get('/control-fichajes/pendientes'),
  getDiasSinFichaje: (params) => api.get('/control-fichajes/dias-sin-fichaje', { params }),
  getContador: () => api.get('/control-fichajes/contador'),
  justificar: (id, data) => api.post(`/control-fichajes/${id}/justificar`, data),
  ignorar: (id, data) => api.post(`/control-fichajes/${id}/ignorar`, data),
  verificarManual: () => api.post('/control-fichajes/verificar-manual'),
  verificarAcceso: () => api.post('/control-fichajes/verificar-acceso')
}

// Festivos
export const festivosAPI = {
  getAll: (params) => api.get('/festivos', { params }),
  create: (data) => api.post('/festivos', data),
  update: (id, data) => api.put(`/festivos/${id}`, data),
  delete: (id) => api.delete(`/festivos/${id}`)
}

// Correcciones
export const correccionesAPI = {
  proponer: (data) => api.post('/correcciones/proponer', data),
  getMisPropuestas: (params) => api.get('/correcciones/mis-propuestas', { params }),
  getMisPropuestasPendientesCount: () => api.get('/correcciones/mis-propuestas/pendientes/count'),
  responder: (id, data) => api.post(`/correcciones/${id}/responder`, data)
}

// Notificaciones
export const notificacionesAPI = {
  getAll: (params) => api.get('/notificaciones', { params }),
  getNoLeidasCount: () => api.get('/notificaciones/no-leidas/count'),
  verificar: () => api.post('/notificaciones/verificar'),
  marcarLeida: (id) => api.put(`/notificaciones/${id}/leer`),
  marcarTodasLeidas: () => api.put('/notificaciones/leer-todas'),
  eliminar: (id) => api.delete(`/notificaciones/${id}`)
}

// Billing / Suscripciones
export const billingAPI = {
  getPlans: () => api.get('/billing/plans'),
  getStatus: () => api.get('/billing/status'),
  createCheckout: (data) => api.post('/billing/checkout', data),
  createPortal: () => api.post('/billing/portal'),
  previewSlots: (slots) => api.get(`/billing/preview-slots?slots=${slots}`),
  addSlots: (data) => api.post('/billing/add-slots', data),
  reduceSlots: (data) => api.post('/billing/reduce-slots', data),
  changePlan: (data) => api.post('/billing/change-plan', data)
}

// Leads (super_admin)
export const leadsAPI = {
  getAll: (params) => api.get('/leads', { params }),
  getStats: () => api.get('/leads/stats'),
  update: (id, data) => api.put(`/leads/${id}`, data),
  exportCSV: () => api.get('/leads/export', { responseType: 'blob' })
}

export const asesoriaAPI = {
  // Para la asesoría autenticada
  getMisClientes: () => api.get('/asesorias/mis-clientes'),
  getCliente: (id) => api.get(`/asesorias/mis-clientes/${id}`),
  // Para super_admin
  getAllVinculos: () => api.get('/asesorias/all-vinculos'),
  getAllAsesorias: () => api.get('/asesorias/all-asesorias'),
  vincular: (data) => api.post('/asesorias/vincular', data),
  updateVinculo: (id, data) => api.put(`/asesorias/vinculos/${id}`, data),
  deleteVinculo: (id) => api.delete(`/asesorias/vinculos/${id}`)
}

export const horasExtrasAPI = {
  check:          (fichajeId) => api.get(`/horas-extras/check/${fichajeId}`),
  crear:          (data) => api.post('/horas-extras', data),
  misSolicitudes: (params) => api.get('/horas-extras/mis-solicitudes', { params }),
  todas:          (params) => api.get('/horas-extras/todas', { params }),
  resumen:        (params) => api.get('/horas-extras/resumen', { params }),
  responder:      (id, data) => api.put(`/horas-extras/${id}/responder`, data),
}

export default api